function readFileEntry(entry) {
    return new Promise((resolve, reject) => {
        entry.file(resolve, reject);
    });
}

function readDirectoryEntries(directoryEntry) {
    const reader = directoryEntry.createReader();
    const entries = [];

    return new Promise((resolve, reject) => {
        const readBatch = () => {
            reader.readEntries((batch) => {
                if (!batch.length) {
                    resolve(entries);
                    return;
                }
                entries.push(...batch);
                readBatch();
            }, reject);
        };

        readBatch();
    });
}

async function expandEntry(entry, parentPath = '') {
    const relativePath = parentPath ? `${parentPath}/${entry.name}` : entry.name;

    if (entry.isFile) {
        const file = await readFileEntry(entry);
        return [{file, relativePath}];
    }

    if (!entry.isDirectory) return [];

    const children = await readDirectoryEntries(entry);
    const nestedFiles = await Promise.all(
        children.map((child) => expandEntry(child, relativePath))
    );
    return nestedFiles.flat();
}

/**
 * Expands dropped directories because DataTransfer.files only exposes the
 * top-level drag items in Chromium/WebKit. Paths are relative to the drag
 * selection, matching the paths produced by an <input webkitdirectory>.
 */
export async function collectDroppedFiles(dataTransfer) {
    const items = Array.from(dataTransfer?.items || []);
    const entries = [];
    const fallbackFiles = [];

    items.forEach((item) => {
        if (item.kind && item.kind !== 'file') return;
        const entry = item.webkitGetAsEntry?.();
        if (entry) {
            entries.push(entry);
            return;
        }
        const file = item.getAsFile?.();
        if (file) fallbackFiles.push({file, relativePath: file.name});
    });

    if (!entries.length) {
        const files = fallbackFiles.length
            ? fallbackFiles
            : Array.from(dataTransfer?.files || []).map((file) => ({
                file,
                relativePath: file.webkitRelativePath || file.name,
            }));
        return files;
    }

    // Dropping a single folder means that folder is the selected root. When
    // several siblings are dropped, their names are part of the upload layout
    // (for example: episode.mcap + episode/).
    const stripSingleRootDirectory = entries.length === 1
        && fallbackFiles.length === 0
        && entries[0].isDirectory;

    const expanded = await Promise.all(entries.map(async (entry) => {
        const files = await expandEntry(entry);
        if (!stripSingleRootDirectory) return files;
        const rootPrefix = `${entry.name}/`;
        return files.map((item) => ({
            ...item,
            relativePath: item.relativePath.startsWith(rootPrefix)
                ? item.relativePath.slice(rootPrefix.length)
                : item.relativePath,
        }));
    }));

    return [...expanded.flat(), ...fallbackFiles];
}

